import { Layout, LayoutProps, SiderProps } from "antd";
import React, { Children, forwardRef, ReactNode } from "react";
import { CompoundComponent, getCompoundComponentPropsFactory } from "../libs/utils";

const SPLIT_LAYOUT_CONTENT = "SplitLayout.Content";
const SPLIT_LAYOUT_SIDER = "SplitLayout.Sider";

/**
 * Assign `displayName` to make compound components HMR / Fast Refresh safe.
 * This lets us reliably identify children even if function references change.
 */

// ---------- Sub components ----------

export const Content: CompoundComponent<LayoutProps> = (props) => null;
Content.displayName = SPLIT_LAYOUT_CONTENT;

export const Sider: CompoundComponent<SiderProps> = (props) => null;
Sider.displayName = SPLIT_LAYOUT_SIDER;

// ---------- Props ----------
export type SplitLayoutProps = LayoutProps & {
  children: ReactNode;
};

// ---------- Parent ----------
const SplitLayoutTemplate = forwardRef<HTMLDivElement, SplitLayoutProps>((props, ref) => {
  const { children, ...restProps } = props;

  const getCompoundComponentProps = getCompoundComponentPropsFactory(children);

  const contentProps = getCompoundComponentProps(SPLIT_LAYOUT_CONTENT);
  const { children: contentChildren, ...restContentProps } = contentProps ?? {};

  const siderProps = getCompoundComponentProps(SPLIT_LAYOUT_SIDER);
  const { children: siderChildren, ...restSiderProps } = siderProps ?? {};

  const hasTruthySiderChildren = Boolean(Children.toArray(siderChildren).filter(Boolean).length);

  return (
    <Layout ref={ref} {...restProps}>
      <Layout.Content {...restContentProps}>{contentChildren}</Layout.Content>
      {hasTruthySiderChildren && (
        <Layout.Sider {...restSiderProps}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>{siderChildren}</div>
        </Layout.Sider>
      )}
    </Layout>
  );
});

// ---------- Compound export ----------
export const SplitLayout = Object.assign(SplitLayoutTemplate, {
  Content,
  Sider,
});
